/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import proto_chainImg from 'assets/images/proto-chain.png'
import proto_chain_schemaImg from 'assets/images/proto-chain-schema.png'
import new_operatorImg from 'assets/images/new-operator.png'
import mango_prototypeImg from 'assets/images/mango-prototype.png'
import mango_poly_instImg from 'assets/images/mango-poly-inst.png'
import constructorImg from 'assets/images/constructor.png'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Прототипы</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Прототип объекта</h3>
      <p className={styles.description}>
        ООП в JavaScript построено на прототипном наследовании. Объекты можно организовать в цепочки так, чтобы
        свойство, не найденное в одном объекте, автоматически искалось бы в другом. Связующим звеном выступает
        специальное скрытое свойство <code className={styles.code}>[[Prototype]]</code>, в консоли оно отображается как{' '}
        <code className={styles.code}>__proto__</code>.
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.1 - Прототип</h4>
        <p className={styles.description}>
          Если один объект имеет специальную ссылку в поле <code className={styles.code}>__proto__</code> на другой
          объект, то при чтении свойства из него, если искомое свойство отсутствует в самом объекте, оно ищется в
          объекте на который ссылается <code className={styles.code}>__proto__</code>.{' '}
        </p>
        <CopyBlock
          text={`const animal = { eats: true };
const dog = { barks: true };

dog.__proto__ = animal;

// В dog можно найти оба свойства
console.log(dog.barks); // true
console.log(dog.eats); // true`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Первый лог работает очевидным образом — он выводит свойство <code className={styles.code}>barks</code> объекта{' '}
          <code className={styles.code}>dog</code>. Второй лог хочет вывести{' '}
          <code className={styles.code}>dog.eats</code>, ищет его в самом объекте{' '}
          <code className={styles.code}>dog</code>, не находит и продолжает поиск в объекте по ссылке из{' '}
          <code className={styles.code}>dog.__proto__</code>, то есть, в данном случае, в{' '}
          <code className={styles.code}>animal</code>.{' '}
        </p>
        <p className={styles.notificationStudy}>
          Объект, на который указывает ссылка в <code className={styles.code}>__proto__</code>, называется прототипом. В
          данном примере animal это <b>прототип</b> для <code className={styles.code}>dog</code>.{' '}
        </p>
        <p className={styles.description}>
          Если мы добавим объекту <code className={styles.code}>dog</code> свойство{' '}
          <code className={styles.code}>eats</code> и присвоим ему <code className={styles.code}>false</code>, то
          результат будет следующим.{' '}
        </p>
        <CopyBlock
          text={`const animal = { eats: true };
const dog = { barks: true, eats: false };

dog.__proto__ = animal;

console.log(dog.barks); // true
console.log(dog.eats); // false, свойство взято из dog`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Другими словами, прототип — это <b>резервное хранилище свойств и методов объекта</b>, автоматически
          используемое при поиске. У объекта, который выступает прототипом может также быть свой прототип, у того свой,
          и так далее. При этом свойства будут искаться по цепочке наследования.{' '}
        </p>
        <p className={styles.description}>
          В спецификации свойство <code className={styles.code}>__proto__</code> обозначено как{' '}
          <code className={styles.code}>[[Prototype]]</code>. Двойные квадратные скобки здесь важны, они указывают на
          то, что это внутреннее, служебное свойство.{' '}
        </p>
        <p className={styles.notificationWarn}>
          Несмотря на то, что свойство <code className={styles.code}>__proto__</code> на сегодняшний день
          стандартизовано, и проще для объяснения материала, его изменение напрямую является грубым нарушением. На
          практике используются методы для манипуляции с прототипами, такие как{' '}
          <code className={styles.code}>Object.create()</code>,{' '}
          <code className={styles.code}>Object.getPrototypeOf()</code>,{' '}
          <code className={styles.code}>Object.setPrototypeOf()</code> и другие.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.2 - Object.create()</h4>
        <p className={styles.description}>
          Для того чтобы правильно задать прототип объекта, можно использовать метод{' '}
          <code className={styles.code}>Object.create(obj)</code>, передав параметром{' '}
          <code className={styles.code}>obj</code> ссылку на объект который мы хотим сделать прототипом для создаваемого
          объекта.{' '}
        </p>
        <CopyBlock
          text={`const animal = { eats: true };
const dog = Object.create(animal);
dog.barks = true;

console.log(dog.barks); // true
console.log(dog.eats); // true`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          На рисунке ниже видно то, что называется <b>цепочкой прототипов (prototype chain)</b>. В свойство{' '}
          <code className={styles.code}>__proto__</code> объекта <code className={styles.code}>dog</code> записана
          ссылка на объект <code className={styles.code}>animal</code>, в свойство{' '}
          <code className={styles.code}>__proto__</code> которого, в свою очередь, записана ссылка на родителя всех
          объектов <code className={styles.code}>Object</code>. Именно поэтому мы можем вызывать методы вроде{' '}
          <code className={styles.code}>hasOwnProperty</code> или <code className={styles.code}>toString</code>, хотя мы
          не определяли их для <code className={styles.code}>dog</code> или <code className={styles.code}>animal</code>.{' '}
        </p>
        <img className={styles.img} src={proto_chainImg} alt="proto-chain" />
        <p className={styles.description}>
          Механизм поиска свойства работает до первого совпадения. Интерпретатор ищет свойство по имени в объекте, если
          не находит, то обращается к свойству <code className={styles.code}>[[Prototype]]</code>, т.е. переходит по
          ссылке к объекту-прототипу, а затем и прототипу прототипа.{' '}
        </p>
        <p className={styles.description}>
          В конце этой цепочки находится <code className={styles.code}>null</code>. В случае первого совпадения будет
          возвращено значение свойства. Если интерпретатор доберется до конца цепочки и не найдет свойства с таким
          ключом, то вернет <code className={styles.code}>undefined</code>.{' '}
        </p>
        <img className={styles.img} src={proto_chain_schemaImg} alt="proto-chain-schema" />
        <p className={styles.notificationStudy}>
          Данный механизм называется динамическая диспетчеризация (dynamic dispatch) или делегация (delegation). В
          отличие от статической дипетчеризации, когда ссылки разрешаются во время компиляции, динамическая
          диспетчеризация всегда разрешает ссылки во время исполнения программы.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.3 - Object.prototype.hasOwnProperty()</h4>
        <p className={styles.description}>
          После того как мы узнали о том как происходит поиск свойств объекта, должно стать понятно, почему цикл{' '}
          <code className={styles.code}>for...in</code> не делает различия между свойствами объекта и его прототипа.
        </p>
        <CopyBlock
          text={`const animal = { eats: true };
const dog = Object.create(animal);
dog.barks = true;

for (const key in dog) {
  console.log(key); // barks, eats
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Именно поэтому мы используем метод <code className={styles.code}>obj.hasOwnProperty(prop)</code>, который
          возвращает <code className={styles.code}>true</code>, если свойство <code className={styles.code}>prop</code>{' '}
          принадлежит самому объекту <code className={styles.code}>obj</code>, а не его прототипу, иначе{' '}
          <code className={styles.code}>false</code>.{' '}
        </p>
        <CopyBlock
          text={`const animal = { eats: true };
const dog = Object.create(animal);
dog.barks = true;

for (const key in dog) {
  if (!dog.hasOwnProperty(key)) continue;

  console.log(key); // barks
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Метод <code className={styles.code}>Object.keys(obj)</code> вернет массив только собственных ключей объекта{' '}
          <code className={styles.code}>obj</code>, поэтому рекомендуется использовать именно его.
        </p>
        <CopyBlock
          text={`const animal = { eats: true };
const dog = Object.create(animal);
dog.barks = true;

const dogKeys = Object.keys(dog);

console.log(dogKeys); // ['barks']`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Свойство Function.prototype</h3>
      <p className={styles.description}>
        Мы уже знаем что такое прототип объекта, свойство <code className={styles.code}>__proto__</code>, как происходит
        поиск отсутствующих свойств объекта по цепочке и методы указания прототипа для одного объекта. Но в реальных
        задачах объекты создаются динамически, то есть функцией-конструктором, через{' '}
        <code className={styles.code}>new</code>. Давайте разберемся как происходит указание прототипа в этом случае.{' '}
      </p>
      <p className={styles.description}>
        Создадим функцию-конструктор <code className={styles.code}>Guest</code> которая будет создавать нам экземпляры
        объектов гостя отеля.{' '}
      </p>
      <CopyBlock
        text={`const Guest = function(name, room) {
  this.name = name;
  this.room = room;
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.notificationInfo}>
        Так как функция это тоже объект, у каждой функции, кроме стрелочных, есть свойство{' '}
        <code className={styles.code}>prototype</code>, в котором изначально хранится объект с единственным полем{' '}
        <code className={styles.code}>constructor</code>, указывающим на саму функцию-конструктор.
      </p>
      <p className={styles.description}>
        Свойство <code className={styles.code}>Function.prototype</code>:{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Является объектом</li>
        <li className={styles.list__item}>В него можно записывать свойства и методы</li>
        <li className={styles.list__item}>
          Свойства и методы <code className={styles.code}>prototype</code> будут доступны по ссылке{' '}
          <code className={styles.code}>__proto__</code> объекта
        </li>
        <li className={styles.list__item}>
          У свойства <code className={styles.code}>prototype</code> изначально есть метод{' '}
          <code className={styles.code}>constructor</code>
        </li>
      </ul>
      <CopyBlock
        text={`const Guest = function(name, room) {
  this.name = name;
  this.room = room;
};

console.log(Guest.prototype); // {constructor: ƒ}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        При создании объекта через <code className={styles.code}>new</code>, в его поле{' '}
        <code className={styles.code}>__proto__</code> записывается ссылка на объект хранящийся в свойстве{' '}
        <code className={styles.code}>prototype</code> функции-конструктора.{' '}
      </p>
      <img className={styles.img} src={new_operatorImg} alt="new-operator" />
      <CopyBlock
        text={`const Guest = function(name, room) {
  this.name = name;
  this.room = room;
};

const mango = new Guest('Mango', 28);

console.log(mango);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <img className={styles.img} src={mango_prototypeImg} alt="mango-prototype" />
      <p className={styles.description}>
        Эту особенность мы можем использовать для того, чтобы добавлять в объект{' '}
        <code className={styles.code}>prototype</code> методы, которые будут доступны по ссылке абсолютно всем объектам
        созданным через <code className={styles.code}>new Guest(...)</code>.{' '}
      </p>
      <p className={styles.description}>
        Причем если мы создадим миллион экземпляров гостя, набор методов будет не у каждого свой, а всего один, общий,
        хранящийся в свойстве <code className={styles.code}>Guest.prototype</code> и доступный всем потомкам по ссылке,
        которая записывается в поле <code className={styles.code}>__proto__</code> объекта при создании, из-за
        прототипного наследования и цепочки прототипов.{' '}
      </p>
      <CopyBlock
        text={`const Guest = function(name, room) {
  this.name = name;
  this.room = room;
};

Guest.prototype.showGuestInfo = function() {
  console.log(\`name: $ {this.name}, room: $ {this.room}\`);
};

const mango = new Guest('Mango', 28);
const poly = new Guest('Poly', 36);

mango.showGuestInfo(); // name: Mango, room: 28
poly.showGuestInfo(); // name: Poly, room: 36`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <img className={styles.img} src={mango_poly_instImg} alt="mango-poly-inst" />
      <p className={styles.description}>
        Так как в свойстве <code className={styles.code}>prototype</code> лежит объект, то при прототипном наследование
        происходит присвоение по ссылке, поэтому если мы изменим значение у свойства{' '}
        <code className={styles.code}>prototype</code>, то это новое значение получат и все свойства, имеющие ссылку на
        объект <code className={styles.code}>prototype</code>.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.1 - Свойство constructor</h4>
        <p className={styles.description}>
          Было упоминание того, что по умолчанию, объект в свойстве <code className={styles.code}>prototype</code> уже
          содержит поле <code className={styles.code}>constructor</code>. Запишем это поле явно:{' '}
        </p>
        <CopyBlock
          text={`const Guest = function(name, room) {
  this.name = name;
  this.room = room;
};

Guest.prototype = {
  constructor: Guest,
};`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          В коде выше мы создали свойство <code className={styles.code}>Guest.prototype</code> вручную, но абсолютно
          такой же объект генерируется автоматически. А свойство <code className={styles.code}>constructor</code>{' '}
          содержит ссылку на саму функцию-конструктор.{' '}
        </p>
        <img className={styles.img} src={constructorImg} alt="constructor" />
        <p className={styles.description}>
          Свойство <code className={styles.code}>[[Prototype]]</code> объекта называют скрытым свойством, потому что
          прямой доступ к нему ограничен средствами самого языка. Метод который делает запись ссылки в объект в момент
          создания имеет такой доступ. Находится этот метод в объекте <code className={styles.code}>prototype</code>{' '}
          функции и называется <code className={styles.code}>constructor</code> 🙂.{' '}
        </p>
        <a
          className={styles.link}
          href="http://yehudakatz.com/2011/08/12/understanding-prototypes-in-javascript/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Understanding Prototypes in JavaScript
        </a>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Наследование и конструкторы</h3>
      <p className={styles.description}>
        Конструктор это функция для создания объектов по шаблону. Оператор new создает объект и вызывает
        функцию-конструктор в контексте этого объекта. На выходе получаем объект с полями указанными в
        функции-конструкторе через <code className={styles.code}>this</code> и полем{' '}
        <code className={styles.code}>[[Prototype]]</code> которое содержит ссылку на поле{' '}
        <code className={styles.code}>prototype</code> функции-конструктора.{' '}
      </p>
      <p className={styles.description}>
        Иногда случаются задачи когда объекты созданные функцией-конструктором должны также иметь доступ к полям и
        методам прототипа объявленым в другой функции-конструкторе.{' '}
      </p>
      <p className={styles.description}>
        Например мы пишем игру в стиле RPG, и нам необходимо подготовить логику для классовой системы персонажей, где
        есть общий конструктор <code className={styles.code}>Hero</code> с дефолтными полями общими для всех классов,
        вроде имени, здоровья, количества опыта и т. п. После чего нам необходимо сделать конструкторы для{' '}
        <code className={styles.code}>Warrior</code> и <code className={styles.code}>Wizard</code>, экземпляры которых
        также должны иметь доступ к полям <code className={styles.code}>Hero</code>, но в тоже время иметь свои
        собственные.{' '}
      </p>
      <p className={styles.description}>Давайте реализуем это используя прототипное наследование. </p>
      <CopyBlock
        text={`const Hero = function(name, xp) {
  this.name = name;
  this.xp = xp;
};

/*
 * Теперь у нас есть конструктор базового класса героя,
 * добавим в prototype какой-то метод.
 */
Hero.prototype.gainXp = function(amount) {
  console.log(\`$ {this.name} gained $ {amount} experience points\`);
  this.xp += amount;
};

const mango = new Hero('Mango', 1000);
console.log(mango); // Hero { name: 'Mango', xp: 1000 }

// Так как mango это экземпляр Hero, то ему доступны методы из Hero.prototype
mango.gainXp(500); // Mango gained 500 experience points
console.log(mango); // Hero { name: 'Mango', xp: 1500 }`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Далее необходимо создать класс <code className={styles.code}>Warrior</code>, так как нет смысла добавлять в{' '}
        <code className={styles.code}>Hero</code> абсолютно все поля всех классов. Поэтому нам необходимо создать еще
        функцию-конструктор но при этом она должна быть как-то связана с <code className={styles.code}>Hero</code>.{' '}
      </p>
      <p className={styles.description}>
        Для решения этой задачи мы можем использовать метод <code className={styles.code}>call()</code>, вызвав
        функцию-конструктор <code className={styles.code}>Hero</code> и передав ей объект создающийся в{' '}
        <code className={styles.code}>Warrior</code> как контекст.{' '}
      </p>
      <CopyBlock
        text={`const Warrior = function(name, xp, weapon) {
  /*
   * Во время выполнения функции Warrior вызываем функцию Hero
   * в контексте объекта создающегося в Warrior, а так же передаем
   * аргументы для инициализации полей this.name и this.xp
   *
   * this это будущий экземпляр Warrior
   */
  Hero.call(this, name, xp);

  // Тут добавляем новое свойство - оружие
  this.weapon = weapon;
};

// Сразу добавим метод для атаки в prototype воина
Warrior.prototype.attack = function() {
  console.log(\`$ {this.name} attacks with $ {this.weapon}\`);
};

const poly = new Warrior('Poly', 200, 'sword');

console.log(poly); // Warrior {name: "Poly", xp: 200, weapon: "sword"}
poly.attack(); // Poly attacks with sword`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Вроде все хорошо, но что произойдет если мы попробуем вызвать у <code className={styles.code}>poly</code> метод{' '}
        <code className={styles.code}>gainXp()</code>, который обьявлен на{' '}
        <code className={styles.code}>Hero.prototype</code>? — будет ошибка{' '}
      </p>
      <CopyBlock
        text={`poly.gainXp(); // Uncaught TypeError: poly.gainXp is not a function`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Дело в том что поля из <code className={styles.code}>Hero.prototype</code> не добавляются в цепочку прототипов
        по умолчанию. Необходимо явно укзать связь поля <code className={styles.code}>Warrior.prototype</code> и{' '}
        <code className={styles.code}>Hero.prototype</code>. Сделать это очень легко, но важно понимать как и почему это
        работает.{' '}
      </p>
      <CopyBlock
        text={`/*
 * Используем Object.create() для того чтобы изначально записать
 * в Warrior.prototype пустой объект, в __proto__ которого будет
 * ссылка на Hero.prototype. Это необходимо сделать до того
 * как добавлять методы
 */
Warrior.prototype = Object.create(Hero.prototype);

// Не забываем добавить в Warrior.prototype свойство constructor
Warrior.prototype.constructor = Warrior;

// Добавим метод для атаки
Warrior.prototype.attack = function() {
  console.log(\`$ {this.name} attacks with $ {this.weapon}\`);
};

// Попробуем теперь
poly.gainXp(300); // Poly gained 300 experience points`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        В результате мы получили цепочку прототипов. При вызове <code className={styles.code}>poly.gainXp()</code>, идет
        поиск поля <code className={styles.code}>gainXp</code> в самом объекте <code className={styles.code}>poly</code>
        , если такового нет, тогда идет поиск в том объекте который указан в поле{' '}
        <code className={styles.code}>poly.__proto__</code>, это ссылка на{' '}
        <code className={styles.code}>Warrior.ptototype</code>.{' '}
      </p>
      <p className={styles.description}>
        Eсли же его нету и там, то поиск идет в поле <code className={styles.code}>__proto__</code> того объекта что
        указан в <code className={styles.code}>poly.__proto__</code>, то есть в{' '}
        <code className={styles.code}>poly.__proto__.__proto__</code>, а это ссылка на Hero.prototype, где есть метод{' '}
        <code className={styles.code}>gainXp</code>.{' '}
      </p>
      <p className={styles.description}>Полный код примера. </p>
      <CopyBlock
        text={`const Hero = function(name, xp) {
  this.name = name;
  this.xp = xp;
};

Hero.prototype.gainXp = function(amount) {
  console.log(\`$ {this.name} gained $ {amount} experience points\`);
  this.xp += amount;
};

const Warrior = function(name, xp, weapon) {
  Hero.call(this, name, xp);

  this.weapon = weapon;
};

Warrior.prototype = Object.create(Hero.prototype);
Warrior.prototype.constructor = Warrior;

Warrior.prototype.attack = function() {
  console.log(\`$ {this.name} attacks with $ {this.weapon}\`);
};

const poly = new Warrior('Poly', 200, 'sword');`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.4 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://www.digitalocean.com/community/tutorials/understanding-prototypes-and-inheritance-in-javascript"
            target="_blank"
            rel="noopener noreferrer"
          >
            Understanding Prototypes and Inheritance in JavaScript
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://hackernoon.com/inheritance-in-javascript-21d2b82ffa6f"
            target="_blank"
            rel="noopener noreferrer"
          >
            Inheritance in JavaScript
          </a>
        </li>
      </ul>
    </section>
  </section>
))
